/* eslint-disable max-classes-per-file */

import React, { createRef, Component } from 'react';
import * as R from 'ramda';
import { token } from '@atlaskit/tokens';
import { injectIntl } from '@atlassian/jira-intl';
import { colors, ListSkeleton } from '@atlassian/jira-portfolio-3-common';
import {
	type ListRowProps,
	List,
	WindowScroller,
} from '@atlassian/jira-portfolio-3-common/src/react-virtualized';
import type { ScopeIssue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/scope/types';
import {
	TABLE_ISSUE,
	TABLE_INLINE_CREATE,
	TABLE_HEADER,
	TABLE_GROUP,
	TABLE_GROUP_HEADER,
	TABLE_BLANK_ROW,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/table';
import StickyHeader from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/sticky-header';
import {
	getCurrentValue,
	getOptimizedValue,
	hasValueChanged,
	getRowHighlighter,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/util';
import BaseComponent from '@atlassian/jira-portfolio-3-portfolio/src/common/view/base-component';
import {
	SCOPE_ROW_HEIGHT,
	STICKY_HEADER_CONTAINER_PREFIX,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { manuallyUpdateReactVirtualizedListWidth } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/magic';
import messages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import ErrorBoundary from './error-boundary';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

const cellStyle = (rootIndex: number, isOptimized: boolean): string => {
	const isEven = rootIndex % 2 === 0;

	if (isEven) {
		if (isOptimized) {
			return `${styles.cell} ${styles['cell-optimized']}`;
		}
		return styles.cell;
	}
	if (isOptimized) {
		return `${styles.cell} ${styles['cell-even-optimized']}`;
	}
	return `${styles.cell} ${styles['cell-even']}`;
};

// eslint-disable-next-line jira/react/no-class-components, @typescript-eslint/no-explicit-any
class CellWrapper extends BaseComponent<any, any> {
	static displayName = 'CellWrapper';

	static whyDidYouRender = true;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	shouldComponentUpdate(nextProps: any, nextState: any) {
		return !nextProps.isScrolling && super.shouldComponentUpdate(nextProps, nextState);
	}

	render() {
		const { cell: Cell, ...props } = this.props;

		return (
			<ErrorBoundary>
				<Cell {...props} />
			</ErrorBoundary>
		);
	}
}

// eslint-disable-next-line jira/react/no-class-components
export class Column extends Component<Props> {
	static defaultProps = {
		StickyHeader,
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any, react/sort-comp
	ref = createRef<any>();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	containerRef = createRef<any>();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	listRef = createRef<any>();

	// eslint-disable-next-line react/sort-comp
	shouldComponentUpdate(nextProps: Props) {
		if (nextProps.isDragging && this.props.width !== nextProps.width && this.containerRef.current) {
			manuallyUpdateReactVirtualizedListWidth(this.containerRef.current, nextProps.width);
		}
		return !nextProps.isDragging;
	}

	componentDidMount() {
		this.forceHandleScroll();
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.scrollElement !== prevProps.scrollElement) {
			this.forceHandleScroll();
		}
		const listRef = this.listRef.current;
		if (listRef && prevProps.items !== this.props.items) {
			const length = Math.min(prevProps.items.length, this.props.items.length);
			for (let i = 0; i < length; i++) {
				if (prevProps.items[i].height !== this.props.items[i].height) {
					listRef.recomputeRowHeights();
					break;
				}
			}
		}
	}

	forceHandleScroll() {
		// JPOS-2155 Fields are not rendered until next scrolling event
		// After collapse/expand column item position is wrong because starting index for second part of table starts from 0
		// this workaround is for recalculating items position in a column after colapse/expand field.
		const ref = this.ref.current;
		if (ref) {
			ref.__handleWindowScrollEvent();
			ref.__resetIsScrolling();
			/* eslint-enable */
		}
	}

	getCell = (issue: ScopeIssue, isScrolling: boolean) => {
		const warnings = this.props.warnings.byIssue[issue.id || ''] || [];
		const hasWarnings = warnings.length > 0;

		const {
			cell,
			isReadOnly = false,
			attribute,
			showOptimizations = false,
			width,
			showWarning,
		} = this.props;
		const { id, optimized } = issue;

		const isOptimized =
			showOptimizations && optimized && R.has(attribute, optimized)
				? hasValueChanged(getCurrentValue(issue, attribute), getOptimizedValue(issue, attribute))
				: false;

		const styleName = cellStyle(issue.rootIndex, isOptimized);
		return (
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={styleName}
				key={id}
				data-issue={id}
				data-group={issue.group}
				data-name={`cell-${id}`}
				data-attribute={attribute}
			>
				{getRowHighlighter(issue.group, id, hasWarnings && showWarning)}
				<CellWrapper
					cell={cell}
					issue={issue}
					isReadOnly={isReadOnly}
					isScrolling={isScrolling}
					showOptimizations={showOptimizations}
					width={width}
					attribute={attribute}
				/>
			</div>
		);
	};

	rowRenderer = ({
		key,
		index,
		style,
		isScrolling,
	}: Pick<ListRowProps, 'key' | 'index' | 'style' | 'isScrolling'>) => {
		const { items } = this.props;
		const item = items[index];
		const { tag } = item;
		switch (item.tag) {
			case TABLE_ISSUE: {
				const issue: ScopeIssue = item.value;
				return (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div key={`${item.tag}-${item.group || ''}-${issue.id}`} style={style}>
						{this.getCell(issue, isScrolling)}
					</div>
				);
			}
			case TABLE_INLINE_CREATE: {
				const issue: ScopeIssue = item.value;
				return (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div key={`${item.tag}-${item.group || ''}-${issue.id}`} style={style}>
						{this.getCell(issue, isScrolling)}
					</div>
				);
			}
			case TABLE_HEADER: {
				return (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div key={key} style={style}>
						<div
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								height: 40,
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								backgroundColor: token('color.background.neutral.subtle', colors.N0),
							}}
						/>
					</div>
				);
			}
			case TABLE_GROUP_HEADER: {
				return (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div key={key} style={style}>
						<div
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								height: 40,
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								backgroundColor: token('color.background.neutral.subtle', colors.N0),
							}}
						/>
					</div>
				);
			}
			case TABLE_BLANK_ROW: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				return <div key={key} style={style} className={styles.blankRow} />;
			}

			case TABLE_GROUP: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				return <div key={key} style={style} className={styles.groupRow} />;
			}
			default: {
				throw new Error(`Unexpected TABLE item variant: ${tag}`);
			}
		}
	};

	render() {
		const {
			intl,
			attribute,
			items,
			mode,
			scrollElement,
			warnings,
			width,
			isLoading,
			id,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			StickyHeader,
		} = this.props;
		// NOTE rows={items} + warnings={warnings} are passed to List to trigger re-render when items content was changed
		// but length remained the same
		const content = scrollElement ? (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div ref={this.containerRef} className={styles.column} data-testid={`column-${attribute}`}>
				<WindowScroller scrollElement={scrollElement} ref={this.ref}>
					{({ height = 0, isScrolling, onChildScroll, scrollTop }) => (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						<div style={{ position: 'relative' }}>
							{StickyHeader && (
								<StickyHeader
									sectionId="field"
									containerId={`${STICKY_HEADER_CONTAINER_PREFIX}-field-${id}`}
								/>
							)}
							<List
								ref={this.listRef}
								autoHeight
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={styles.list}
								height={height}
								isScrolling={isScrolling}
								mode={mode}
								onScroll={onChildScroll}
								rowCount={items.length}
								rowHeight={({ index }) => {
									// eslint-disable-next-line @typescript-eslint/no-shadow
									const height = items[index].height || 1;
									return height * SCOPE_ROW_HEIGHT;
								}}
								rowRenderer={this.rowRenderer}
								rows={items}
								scrollTop={scrollTop}
								tabIndex={null}
								width={width}
								warnings={warnings}
								overscanRowCount={20}
							/>
						</div>
					)}
				</WindowScroller>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={styles.space} />
			</div>
		) : null;

		return isLoading ? (
			<ListSkeleton
				ariaLabel={intl.formatMessage(messages.loadingIssuesMessage)}
				numOfRows={13}
				spaceInBetween={12}
			/>
		) : (
			content
		);
	}
}

export default injectIntl(Column);
